<template>
    <div class="cal">
        <div class="cal__toolbar">
            <div class="cal__toolbar-left">
                <button
                    class="cal__btn"
                    @click.prevent="changeView('week')"
                    :disabled="loading"
                >
                    Weekly
                </button>
                <button
                    class="cal__btn"
                    @click.prevent="changeView('month')"
                    :disabled="loading"
                >
                    Monthly
                </button>
                <button
                    class="cal__btn"
                    @click.prevent="loadAll"
                    :disabled="loading"
                >
                    <i
                        class="fa fa-sync-alt"
                        :class="{'fa-spin': loading}"
                    />
                    <span v-if="loading">Loading...</span>
                </button>
            </div>
            <div class="cal__toolbar-center">
                <div class="cal__toolbar-title">
                    {{ startDate.format('Do MMM') }} - {{ endDate.format('Do MMM YYYY') }}
                </div>
            </div>
            <div class="cal__toolbar-right">
                <button
                    class="cal__btn"
                    @click.prevent="prev"
                    :disabled="loading"
                >
                    &lt; Prev
                </button>
                <button
                    class="cal__btn"
                    @click.prevent="today"
                    :disabled="loading"
                >
                    Today
                </button>
                <date-time-picker
                    v-model="jumpDate"
                    :options="{format: 'L'}"
                    @input="handleJumpToDate"
                />
                <button
                    class="cal__btn"
                    @click.prevent="next"
                    :disabled="loading"
                >
                    Next >
                </button>
            </div>
        </div>
        <div class="cal__dates">
            <div class="cal__dates-container">
                <div class="cal__dates-labels">
                    <div
                        class="cal__dates-resource-label"
                    >
                        Staff
                    </div>
                    <div
                        class="cal__dates-label"
                        v-for="date in dates"
                        :key="'label_'+date.unix()"
                    >
                        {{ date.format('ddd Do') }}
                    </div>
                </div>
                <div class="cal__dates-body">
                    <div
                        class="cal__dates-row"
                        v-for="resource in resources"
                        :key="resource.id"
                    >
                        <div
                            class="cal__dates-resource"
                        >
                            {{ resource.name }}
                        </div>
                        <div
                            :class="{
                                'cal__dates-cell': true,
                                'cal__dates-cell--today': isToday(date),
                                'cal__dates-cell--full': isDayFull(date, resource) && !inPast(date),
                                'cal__dates-cell--future': !inPast(date),
                                'cal__dates-cell--menu-open': isDayOptionsMenuOpen(date, resource),
                            }"
                            v-for="date in dates"
                            :key="'cell_'+date.unix()"
                            @click="$emit('dayClick', {date, resource})"
                        >
                            <div
                                v-for="event in eventsForDay(date, resource)"
                                class="cal__event"
                                :class="[{'cal__event--hover': event.hover, 'cal__event--active': (selectedJob && event.jobId === selectedJob.id) || (openEvent === event), 'cal__event--approved': event.status === 'Approved', 'cal__event--declined': event.status === 'Declined'}, `cal__event-${event.type_key}`]"
                                @click.stop="eventClicked(event, date, resource)"
                                :key="eventKey(event, date)"
                                :data-event="eventKey(event, date)"
                                :style="{'background-color': event.backgroundColor, 'color': event.textColor, 'border-color': event.borderColor}"
                                @mouseover="eventHover(event, true)"
                                @mouseleave="eventHover(event, false)"
                            >
                                <span class="cal__event-title">
                                    <i
                                        v-if="event.type_key === 'early_start'"
                                        class="fa fa-sun"
                                    />
                                    <i
                                        v-if="event.type_key === 'overnight'"
                                        class="fa fa-moon"
                                    />
                                    {{ event.title }}
                                </span>
                            </div>
                            <JobRoutesMapButton
                                v-if="jobsForDay(date, resource).length > 1"
                                :events="jobsForDay(date, resource)"
                                :user="resource"
                            />
                            <DayOptions
                                v-if="!inPast(date)"
                                :date="date"
                                :resource="resource"
                                @menu-toggle="handleDayOptionsMenuToggle(date, resource, $event)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import DateTimePicker from "./DateTimePickerComponent.vue";
import JobRoutesMapButton from "./JobRoutesMapButton.vue";
import DayOptions from "./calendar/DayOptions.vue";
import NProgress from 'nprogress';
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";

const moment = window.moment;

let jobsChannel;
let eventsChannel;

export default {
    name: "Calendar",
    components: {DayOptions, JobRoutesMapButton, DateTimePicker },
    props: {
        selectedJob: {
            required: false,
            type: Object,
            default: null,
        },
        openEvent: {
            required: false,
            type: Object,
            default: () => {return {}}
        },
        showJobs: {
            type: Boolean,
            default: true,
        },
        showAllStaff: {
            type: Boolean,
            default: false,
        },
    },
    data: function () {
        return {
            jumpDate: undefined,
            openDayOptionsMenuKey: null,
        };
    },
    computed: {
        ...mapState('resourceCalendar', [
            'startDate',
            'resources',
            'events',
            'timeSlots',
            'view',
        ]),
        ...mapGetters('resourceCalendar', [
            'endDate',
            'allEvents',
            'dates',
            'loading',
        ]),
    },
    watch: {
        view: {
            immediate: true,
            handler() {
                this.setStartDate(this.startDate.clone().startOf(this.view));
            },
        },
        startDate() {
            this.jumpDate = this.startDate.clone();
        },
        showAllStaff: {
            immediate: true,
            handler(newValue) {
                this.setShowAllStaff(newValue);
                this.loadResources();
                this.loadEvents();
            },
        },
        loading: {
            immediate: true,
            handler(newValue) {
                if (newValue) {
                    NProgress.start();
                    NProgress.set(.7);
                    NProgress.inc();
                    return;
                }

                NProgress.done();
            },
        }
    },
    mounted: function () {
        this.unsubscribeFromEvents();
        this.loadResources();
        this.loadAll();
        this.subscribeToEvents();
    },
    beforeDestroy() {
        this.unsubscribeFromEvents();
    },
    methods: {
        ...mapMutations('resourceCalendar', [
            'setShowAllStaff',
            'clearAll',
            'setStartDate',
        ]),
        ...mapActions('resourceCalendar', [
            'loadAll',
            'loadTimeslots',
            'loadEvents',
            'loadResources',
            'next',
            'prev',
            'today',
            'jumpToDate',
            'changeView',
        ]),
        eventsForDay(date, resource) {
            return this.$store.getters['resourceCalendar/eventsForDay'](date, resource);
        },
        jobsForDay(date, resource) {
            return this.$store.getters['resourceCalendar/jobsForDay'](date, resource);
        },
        unsubscribeFromEvents() {
            window.Echo.leaveChannel('jobs');
            window.Echo.leaveChannel('events');
        },
        subscribeToEvents() {
            jobsChannel = window.Echo.channel('jobs');
            jobsChannel.listen('JobUpdated', (jobUpdatedEvent) => {
                if (jobUpdatedEvent.start && moment(jobUpdatedEvent.start).isBetween(this.startDate, this.endDate)) {
                    this.loadTimeslots();
                }
            })
            eventsChannel = window.Echo.channel('events');
            eventsChannel.listen('CalendarEventChanged', (event) => {
                if (event.start && moment(event.start).isBetween(this.startDate, this.endDate)) {
                    this.loadEvents();
                }
            })
        },

        isToday(date) {
            return date.isSame(window.moment(), 'day');
        },

        inPast(date) {
            return date.isBefore(window.moment(), 'day');
        },

        dayOptionsMenuKey(date, resource) {
            return resource.id + '_' + date.unix();
        },

        isDayOptionsMenuOpen(date, resource) {
            return this.openDayOptionsMenuKey === this.dayOptionsMenuKey(date, resource);
        },

        handleDayOptionsMenuToggle(date, resource, isOpen) {
            const key = this.dayOptionsMenuKey(date, resource);

            if (isOpen) {
                this.openDayOptionsMenuKey = key;
                return;
            }

            if (this.openDayOptionsMenuKey === key) {
                this.openDayOptionsMenuKey = null;
            }
        },

        isDayFull(date, resource) {
            return this.$store.getters['resourceCalendar/isDayFull'](date, resource);
        },

        eventKey(event, date) {
            return event.id + '_' + date.unix();
        },

        eventHover(event, value) {
            event.hover = value;
            this.$forceUpdate();
        },

        eventClicked(event, date, resource) {
            if (!event) {
                this.$emit('dayClick', {date, resource})
                return;
            }
            this.$emit('eventClick', {event, date, resource})
        },
        handleJumpToDate() {
            if (!this.jumpDate) {
                return;
            }

            this.jumpToDate(this.jumpDate);
        },
    }
};
</script>




// WEBPACK FOOTER //
// resources/assets/js/components/ResourceCalendarComponent.vue